<template>
  <div class="cdr-dashboard">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-left">
        <h1 class="page-title">
          <v-icon color="primary" class="mr-2">mdi-clock-outline</v-icon>
          {{ $t('cdr-dashboard.title') }}
        </h1>
        <p class="page-subtitle">{{ $t('cdr-dashboard.subtitle') }}</p>
      </div>
      <div class="header-right">
        <div class="refresh-controls">
          <span class="last-update">
            <v-icon small class="mr-1">mdi-information-outline</v-icon>
            {{ $t('cdr-dashboard.last-update', { time: lastUpdateTime }) }}
          </span>

          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                small
                text
                v-bind="attrs"
                v-on="on"
                class="polling-btn ml-2"
                :color="pollingEnabled ? 'primary' : 'grey'"
              >
                <v-icon small left>{{ pollingEnabled ? 'mdi-sync' : 'mdi-sync-off' }}</v-icon>
                {{ pollingEnabled ? pollingIntervalLabel : $t('cdr-dashboard.polling-off') }}
                <v-icon small right>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="setPollingInterval(5)">
                <v-list-item-title>
                  <v-icon small left :color="POLLING_INTERVAL === 5000 && pollingEnabled ? 'primary' : ''">
                    {{ POLLING_INTERVAL === 5000 && pollingEnabled ? 'mdi-check' : '' }}
                  </v-icon>
                  5 {{ $t('cdr-dashboard.seconds') }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="setPollingInterval(10)">
                <v-list-item-title>
                  <v-icon small left :color="POLLING_INTERVAL === 10000 && pollingEnabled ? 'primary' : ''">
                    {{ POLLING_INTERVAL === 10000 && pollingEnabled ? 'mdi-check' : '' }}
                  </v-icon>
                  10 {{ $t('cdr-dashboard.seconds') }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="setPollingInterval(30)">
                <v-list-item-title>
                  <v-icon small left :color="POLLING_INTERVAL === 30000 && pollingEnabled ? 'primary' : ''">
                    {{ POLLING_INTERVAL === 30000 && pollingEnabled ? 'mdi-check' : '' }}
                  </v-icon>
                  30 {{ $t('cdr-dashboard.seconds') }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="setPollingInterval(60)">
                <v-list-item-title>
                  <v-icon small left :color="POLLING_INTERVAL === 60000 && pollingEnabled ? 'primary' : ''">
                    {{ POLLING_INTERVAL === 60000 && pollingEnabled ? 'mdi-check' : '' }}
                  </v-icon>
                  1 {{ $t('cdr-dashboard.minute') }}
                </v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="togglePolling">
                <v-list-item-title>
                  <v-icon small left :color="!pollingEnabled ? 'primary' : ''">
                    {{ !pollingEnabled ? 'mdi-check' : '' }}
                  </v-icon>
                  {{ $t('cdr-dashboard.polling-disabled') }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-btn
            icon
            small
            :loading="refreshing"
            @click="refreshAll"
            :title="$t('refresh')"
            class="refresh-btn ml-2"
          >
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Actions Required Banner -->
    <div class="actions-banner" v-if="hasActionsRequired">
      <div class="banner-left">
        <div class="banner-icon">
          <v-icon color="orange darken-2">mdi-alert-circle</v-icon>
        </div>
        <div class="banner-content">
          <h3 class="banner-title">{{ $t('cdr-dashboard.actions-required.title') }}</h3>
          <p class="banner-text">
            {{ $t('cdr-dashboard.actions-required.message', { count: actionsRequired.pendingCdrs }) }}
            <button class="link-btn" @click="sortByOldest">
              {{ $t('cdr-dashboard.actions-required.sort-oldest') }}
            </button>
          </p>
        </div>
      </div>
      <div class="banner-right">
        <span class="oldest-badge" v-if="oldestAge">
          {{ $t('cdr-dashboard.actions-required.oldest-label') }} {{ oldestAge }}
        </span>
        <v-btn
          color="primary"
          depressed
          small
          @click="treatMostUrgent"
          :disabled="!backlog?.oldestPendingCdr"
        >
          <v-icon left small>mdi-play</v-icon>
          {{ $t('cdr-dashboard.actions-required.treat-urgent') }}
        </v-btn>
      </div>
    </div>

    <!-- Tabs Navigation -->
    <div class="tabs-container">
      <div class="tabs-nav">
        <button
          class="tab-btn"
          :class="{ 'tab-btn--active': currentTab === 'overview' }"
          @click="goToTab('overview')"
        >
          <v-icon small class="mr-2">mdi-chart-bar</v-icon>
          {{ $t('cdr-dashboard.tabs.overview') }}
        </button>
        <button
          class="tab-btn"
          :class="{ 'tab-btn--active': currentTab === 'pending' }"
          @click="goToTab('pending')"
        >
          <v-icon small class="mr-2">mdi-file-document-outline</v-icon>
          {{ $t('cdr-dashboard.tabs.pending') }}
          <span class="tab-badge" v-if="actionsRequired.pendingCdrs > 0">
            {{ actionsRequired.pendingCdrs }}
          </span>
        </button>
        <button
          class="tab-btn tab-btn--right"
          :class="{ 'tab-btn--active': currentTab === 'history' }"
          @click="goToTab('history')"
        >
          <v-icon small class="mr-2">mdi-history</v-icon>
          {{ $t('cdr-dashboard.tabs.history') }}
        </button>
      </div>

      <!-- Tab Content -->
      <div class="tab-content">
        <cdr-overview-tab
          v-show="currentTab === 'overview'"
          ref="overviewTab"
          :period="period"
          :backlog="backlog"
          @update:period="period = $event"
          @go-to-tab="goToTab"
        />

        <pending-cdrs-tab
          v-show="currentTab === 'pending'"
          ref="pendingTab"
          :backlog="backlog"
          @corrected="onCdrCorrected"
          @cancelled="onCdrCancelled"
        />

        <validation-history-tab
          v-show="currentTab === 'history'"
          ref="historyTab"
        />
      </div>
    </div>
  </div>
</template>

<script>
import PendingCdrRepository from '@repository/PendingCdrRepository'
import CdrOverviewTab from './tabs/CdrOverviewTab.vue'
import PendingCdrsTab from './tabs/PendingCdrsTab.vue'
import ValidationHistoryTab from './tabs/ValidationHistoryTab.vue'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/fr'

dayjs.extend(relativeTime)
dayjs.locale('fr')

export default {
  name: 'CdrDashboard',

  components: {
    CdrOverviewTab,
    PendingCdrsTab,
    ValidationHistoryTab,
  },

  data() {
    return {
      currentTab: 'overview',
      period: '7d',
      actionsRequired: {
        pendingCdrs: 0,
        failedPayments: 0,
        failedJobs: 0,
        total: 0,
      },
      backlog: {
        count: 0,
        oldestPendingCdr: null,
      },
      lastUpdate: new Date(),
      refreshInterval: null,
      refreshing: false,
      pollingEnabled: true,
      POLLING_INTERVAL: 30000, // 30 seconds by default
    }
  },

  computed: {
    hasActionsRequired() {
      return this.actionsRequired.pendingCdrs > 0
    },

    oldestAge() {
      if (!this.backlog?.oldestPendingCdr?.createdAt) return null
      return dayjs(this.backlog.oldestPendingCdr.createdAt).fromNow()
    },

    lastUpdateTime() {
      return dayjs(this.lastUpdate).fromNow()
    },

    pollingIntervalLabel() {
      const seconds = this.POLLING_INTERVAL / 1000
      if (seconds >= 60) {
        return `${seconds / 60} ${this.$t('cdr-dashboard.minute')}`
      }
      return `${seconds}s`
    },
  },

  watch: {
    '$route.query': {
      immediate: true,
      handler(query, oldQuery) {
        // Update current tab from URL
        const tab = query.tab
        const oldTab = oldQuery?.tab
        const newTab = (tab && ['overview', 'pending', 'history'].includes(tab)) ? tab : 'overview'
        const tabChanged = newTab !== this.currentTab

        this.currentTab = newTab

        // Apply filters from URL when navigating (always call to reset filters)
        this.$nextTick(() => {
          const filters = this.extractFiltersFromQuery(query)
          if (this.currentTab === 'pending' && this.$refs.pendingTab) {
            this.$refs.pendingTab.applyFilters(filters)
          } else if (this.currentTab === 'history' && this.$refs.historyTab) {
            this.$refs.historyTab.applyFilters(filters)
          }

          // Refresh tab data when changing tabs
          if (tabChanged) {
            this.refreshCurrentTab()
          }
        })
      },
    },
  },

  mounted() {
    this.refreshAll()
    this.startPolling()
  },

  beforeDestroy() {
    this.stopPolling()
  },

  activated() {
    // Resume polling when component is activated (keep-alive)
    this.startPolling()
  },

  deactivated() {
    // Stop polling when component is deactivated (keep-alive)
    this.stopPolling()
  },

  methods: {
    async loadActionsRequired() {
      try {
        const result = await PendingCdrRepository.getActionsRequired()
        this.actionsRequired = {
          pendingCdrs: result.pendingCdrs || 0,
          failedPayments: result.failedPayments || 0,
          failedJobs: result.failedJobs || 0,
          total: result.total || 0,
        }
      } catch (error) {
        console.error('Failed to load actions required:', error)
      }
    },

    async loadBacklog() {
      try {
        this.backlog = await PendingCdrRepository.getBacklog()
      } catch (error) {
        console.warn('Could not load backlog:', error)
      }
    },

    goToTab(tab, filters = null) {
      // Build query with tab and filters
      const query = {}

      if (tab !== 'overview') {
        query.tab = tab
      }

      // Add filters to URL query
      if (filters) {
        if (filters.issueCodes?.length > 0) {
          query.issueCodes = filters.issueCodes.join(',')
        }
        if (filters.sources?.length > 0) {
          query.sources = filters.sources.join(',')
        }
        if (filters.evseId) {
          query.evseId = filters.evseId
        }
        if (filters.period && filters.period !== 'all') {
          query.period = filters.period
        }
      }

      // Use push to preserve browser history
      this.$router.push({ query }).catch(() => {})
    },

    extractFiltersFromQuery(query) {
      const filters = {}
      if (query.issueCodes) {
        filters.issueCodes = query.issueCodes.split(',')
      }
      if (query.sources) {
        filters.sources = query.sources.split(',')
      }
      if (query.evseId) {
        filters.evseId = query.evseId
      }
      if (query.period) {
        filters.period = query.period
      }
      return filters
    },

    sortByOldest() {
      // Navigate to pending tab, then sort
      this.$router.push({ query: { tab: 'pending' } }).catch(() => {})
      this.$nextTick(() => {
        if (this.$refs.pendingTab) {
          this.$refs.pendingTab.sortByOldest()
        }
      })
    },

    treatMostUrgent() {
      if (this.backlog?.oldestPendingCdr?.id) {
        this.$router.push({
          name: 'pending-cdrs.correction',
          params: { id: this.backlog.oldestPendingCdr.id }
        })
      }
    },

    onCdrCorrected() {
      this.loadActionsRequired()
      this.loadBacklog()
      if (this.$refs.overviewTab) {
        this.$refs.overviewTab.refresh()
      }
    },

    onCdrCancelled() {
      this.loadActionsRequired()
      this.loadBacklog()
      if (this.$refs.overviewTab) {
        this.$refs.overviewTab.refresh()
      }
    },

    // Polling methods
    startPolling() {
      this.stopPolling()
      if (this.pollingEnabled) {
        this.refreshInterval = setInterval(() => {
          this.pollActiveTab()
        }, this.POLLING_INTERVAL)
      }
    },

    stopPolling() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
        this.refreshInterval = null
      }
    },

    async pollActiveTab() {
      // Don't poll history tab - less useful for real-time
      if (this.currentTab === 'history') return

      // Load header data
      await Promise.all([
        this.loadActionsRequired(),
        this.loadBacklog(),
      ])

      // Refresh active tab
      await this.refreshCurrentTab()
      this.lastUpdate = new Date()
    },

    async refreshAll() {
      this.refreshing = true
      try {
        await Promise.all([
          this.loadActionsRequired(),
          this.loadBacklog(),
        ])
        await this.refreshCurrentTab()
        this.lastUpdate = new Date()
      } finally {
        this.refreshing = false
      }
    },

    async refreshCurrentTab() {
      switch (this.currentTab) {
        case 'overview':
          if (this.$refs.overviewTab?.refresh) {
            await this.$refs.overviewTab.refresh()
          }
          break
        case 'pending':
          if (this.$refs.pendingTab?.refresh) {
            await this.$refs.pendingTab.refresh()
          }
          break
        case 'history':
          if (this.$refs.historyTab?.searchData) {
            await this.$refs.historyTab.searchData()
          }
          break
      }
    },

    setPollingInterval(seconds) {
      this.POLLING_INTERVAL = seconds * 1000
      this.pollingEnabled = true
      this.startPolling()
    },

    togglePolling() {
      this.pollingEnabled = !this.pollingEnabled
      if (this.pollingEnabled) {
        this.startPolling()
      } else {
        this.stopPolling()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$primary: #EA4335;
$border-color: #E5E7EB;
$text-muted: #6B7280;
$bg-light: #F3F4F6;

.cdr-dashboard {
  padding: 24px 40px;
  background: $bg-light;
  min-height: 100vh;
}

// Page Header
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
}

.page-subtitle {
  font-size: 14px;
  color: $text-muted;
  margin: 0;
  max-width: 500px;
}

.last-update {
  font-size: 13px;
  color: #9CA3AF;
  display: flex;
  align-items: center;
}

.refresh-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.polling-btn {
  font-size: 12px;
  text-transform: none;
  letter-spacing: normal;
}

.refresh-btn {
  transition: transform 0.3s ease;

  &:hover {
    transform: rotate(180deg);
  }
}

// Actions Banner
.actions-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #FFF7ED;
  border-left: 4px solid #F97316;
  border-radius: 0 8px 8px 0;
  padding: 16px 20px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.banner-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.banner-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #FFEDD5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.banner-title {
  font-size: 16px;
  font-weight: 700;
  color: #9A3412;
  margin: 0 0 4px 0;
}

.banner-text {
  font-size: 14px;
  color: #C2410C;
  margin: 0;
}

.link-btn {
  background: none;
  border: none;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 500;
  margin-left: 8px;

  &:hover {
    color: #9A3412;
  }
}

.banner-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.oldest-badge {
  font-size: 13px;
  font-weight: 500;
  color: #9A3412;
  background: #FFEDD5;
  padding: 6px 12px;
  border-radius: 20px;
}

// Tabs
.tabs-container {
  background: white;
  border-radius: 8px;
  border: 1px solid $border-color;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.tabs-nav {
  display: flex;
  border-bottom: 1px solid $border-color;
}

.tab-btn {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  font-size: 14px;
  font-weight: 500;
  color: $text-muted;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #374151;
  }

  &--active {
    color: $primary;
    border-bottom-color: $primary;
    background: rgba($primary, 0.05);
  }

  &--right {
    margin-left: auto;
  }
}

.tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  margin-left: 8px;
  background: $primary;
  color: white;
  font-size: 11px;
  font-weight: 700;
  border-radius: 10px;
}

.tab-content {
  min-height: 400px;
}
</style>
